import { useState, useRef, useEffect } from "react";
import type { Device, ThemeClasses } from "../types";
import { brandTextColor } from "../../../../../../config/theme";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { ICON_BTN } from "../../../../shared";

const ACTIVE_STATUS_ID = "R5tW0uX4yY9cF6dH2aI8";
const INACTIVE_STATUS_ID = "S6uX1vY5zZ0dG7eI3bJ9";

interface DeviceActionsMenuProps {
  device: Device;
  themeClasses: ThemeClasses;
  onDetails: (device: Device) => void;
  onEdit: (device: Device) => void;
  onActivate?: (device: Device) => void;
  onDeactivate?: (device: Device) => void;
  onDelete?: (device: Device) => void;
  onTransfer?: (device: Device) => void;
  t: (key: string, options?: any) => string;
  fullWidth?: boolean;
}

export function DeviceActionsMenu({
  device,
  themeClasses,
  onDetails,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onTransfer,
  t,
  fullWidth = false,
}: DeviceActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { isReseller } = useIsReseller();
  const disableCRUD = useDisableCRUD();
  const isCentralGPS = import.meta.env.VITE_THEME === "central-gps";

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const canEdit = isReseller || isCentralGPS;
  const statusId = device.status?.id;
  const itemClass = `w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${themeClasses.text} hover:bg-primary-500/10 hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed`;

  const run = (fn?: (d: Device) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    fn?.(device);
  };

  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={ref}>
      {fullWidth ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          className={`w-full justify-center flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors text-white bg-gray-700 hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 ${
            open ? "ring-2 ring-primary-500/40" : ""
          }`}
        >
          <i className="ri-settings-3-line text-base"></i>
          <span>{t("devices.actions.menu")}</span>
          <i
            className={`ri-arrow-down-s-line text-base transition-transform ${
              open ? "rotate-180" : ""
            }`}
          ></i>
        </button>
      ) : (
        // En la fila compacta el botón gris de ~110 px con texto pasa a un
        // ícono de 32 px: es lo que más ancho le quitaba a las columnas.
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t("devices.actions.menu")}
          title={t("devices.actions.menu")}
          className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${
            open ? "ring-2 ring-primary-500/40" : ""
          }`}
        >
          <i className="ri-more-2-fill"></i>
        </button>
      )}

      {open && (
        <div
          className={`absolute right-0 z-30 mt-1 w-52 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} shadow-xl py-1 overflow-hidden`}
        >
          <button type="button" className={itemClass} onClick={run(onDetails)}>
            <i className="ri-eye-line" style={{ color: brandTextColor }}></i>
            {t("devices.actions.details")}
          </button>

          {canEdit && (
            <button
              type="button"
              disabled={disableCRUD}
              className={itemClass}
              onClick={run(onEdit)}
            >
              <i className={`ri-pencil-line ${themeClasses.textSecondary}`}></i>
              {t("devices.actions.edit")}
            </button>
          )}

          {isReseller && onActivate && statusId === INACTIVE_STATUS_ID && (
            <button
              type="button"
              disabled={disableCRUD}
              className={itemClass}
              onClick={run(onActivate)}
            >
              <i className="ri-check-line text-green-500"></i>
              {t("devices.actions.activate")}
            </button>
          )}

          {isReseller && onDeactivate && statusId === ACTIVE_STATUS_ID && (
            <button
              type="button"
              disabled={disableCRUD}
              className={itemClass}
              onClick={run(onDeactivate)}
            >
              <i className="ri-close-circle-line text-orange-500"></i>
              {t("devices.actions.deactivate")}
            </button>
          )}

          {isReseller && onTransfer && (
            <button
              type="button"
              disabled={disableCRUD}
              className={itemClass}
              onClick={run(onTransfer)}
            >
              <i className="ri-arrow-left-right-line text-sky-500"></i>
              {t("devices.actions.transfer")}
            </button>
          )}

          {isReseller && onDelete && (
            <>
              <div className={`my-1 border-t ${themeClasses.cardBorder}`}></div>
              <button
                type="button"
                disabled={disableCRUD}
                className={itemClass}
                onClick={run(onDelete)}
              >
                <i className="ri-delete-bin-line text-red-500"></i>
                {t("devices.actions.delete")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
